import type { ModelSelection, ThreadProviderRateLimitStatus } from "@cmmd-forge/contracts";
import { useProviderUsageRecovery } from "~/usage/useProviderUsage";
import {
  resolveEffectiveAccountId,
  resolveSwitchTargetWithHeadroom,
} from "./rateLimitBannerSelection";
import { useActiveRateLimitWindow } from "./useActiveRateLimitWindow";
import { RateLimitWarningBanner } from "./RateLimitWarningBanner";
import { UsageLimitRecoveryDialog, type UsageLimitRecoveryProps } from "./UsageLimitRecoveryDialog";
import { isProviderRateOrQuotaError } from "@cmmd-forge/shared/publicProviderCopy";

interface RateLimitBannerProps extends UsageLimitRecoveryProps {
  readonly threadError?: string | null;
  readonly rateLimitStatus: ReadonlyArray<ThreadProviderRateLimitStatus> | undefined;
}

export function RateLimitBanner(props: RateLimitBannerProps) {
  if (props.modelSelection.provider !== "codex" && props.modelSelection.provider !== "claudeAgent")
    return null;
  return (
    <RateLimitRecovery
      {...props}
      modelSelection={props.modelSelection}
      provider={props.modelSelection.provider}
    />
  );
}

function RateLimitRecovery(
  props: Omit<RateLimitBannerProps, "modelSelection"> & {
    readonly provider: "codex" | "claudeAgent";
    readonly modelSelection: Extract<
      ModelSelection,
      { readonly provider: "codex" | "claudeAgent" }
    >;
  },
) {
  const usage = useProviderUsageRecovery(props.provider);
  const effectiveAccountId = resolveEffectiveAccountId({
    provider: props.provider,
    currentProviderAccountId: props.modelSelection.providerAccountId,
    accounts: usage.accounts.map(({ account }) => account),
  });
  const status = useActiveRateLimitWindow({
    provider: props.provider,
    effectiveAccountId,
    rateLimitStatus: props.rateLimitStatus,
    accounts: usage.accounts.map(({ account }) => account),
  });
  if (isProviderRateOrQuotaError(props.threadError) || status?.status === "rejected") {
    return (
      <UsageLimitRecoveryDialog
        key={props.threadId}
        {...props}
        usage={usage}
        effectiveAccountId={effectiveAccountId}
      />
    );
  }
  if (!status) return null;
  const otherAccount = resolveSwitchTargetWithHeadroom({
    provider: props.provider,
    effectiveAccountId,
    accounts: usage.accounts.map(({ account }) => account),
    usageAccounts: usage.accounts,
  });
  const credits =
    usage.accounts.find(
      ({ account }) =>
        account.provider === props.provider && account.providerAccountId === effectiveAccountId,
    )?.snapshot?.credits ?? null;
  return (
    <RateLimitWarningBanner
      {...props}
      status={status}
      otherAccount={otherAccount}
      credits={credits}
      inventoryState={usage.isError ? "error" : usage.isPending ? "loading" : "ready"}
      onRetryInventory={usage.retry}
    />
  );
}
